import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import {
  FileSignature,
  FileCheck,
  FileX,
  CalendarClock,
  Building2,
  Shield,
  Download,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import {
  getPublicVendorContract,
  signPublicVendorContract,
  refusePublicVendorContract,
  publicVendorContractPdfUrl,
  type PublicVendorContractResponse,
  type PublicVendorContractStatus,
} from "@/services/public-vendor-contract"
import { serverDetail } from "@/services/public-fetch"

// Lien devenu invalide (410), quelle qu'en soit la cause.
const HTTP_GONE = 410
const GONE_TITLE = "Ce lien de signature n'est plus valide"
const GONE_FALLBACK =
  "Ce lien de signature n'est plus valide (expiré ou révoqué par l'organisme). Demandez un nouveau lien."

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return ""
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export default function PublicVendorContractPage() {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [signerName, setSignerName] = useState("")
  const [consentChecked, setConsentChecked] = useState(false)
  const [isRefusing, setIsRefusing] = useState(false)
  const [refuseReason, setRefuseReason] = useState("")

  const query = useQuery({
    queryKey: ["public-vendor-contract", token],
    queryFn: () => getPublicVendorContract(token!),
    enabled: Boolean(token),
    retry: false,
  })

  const signMutation = useMutation({
    mutationFn: () => signPublicVendorContract(token!, signerName.trim()),
    onSuccess: (response) => {
      toast({ title: "Contrat signé", description: "Merci, votre signature a été enregistrée." })
      const signedAt = response.signed_at ?? new Date().toISOString()
      queryClient.setQueryData<PublicVendorContractResponse>(
        ["public-vendor-contract", token],
        (prev) => (prev ? { ...prev, status: "SIGNED", signed_at: signedAt } : prev),
      )
    },
    onError: (err: Error) => {
      toast({ title: "Signature impossible", description: err.message, variant: "destructive" })
    },
  })

  const refuseMutation = useMutation({
    mutationFn: () => refusePublicVendorContract(token!, refuseReason.trim()),
    onSuccess: () => {
      toast({ title: "Contrat refusé", description: "Votre refus a été transmis à l'organisme." })
      queryClient.setQueryData<PublicVendorContractResponse>(
        ["public-vendor-contract", token],
        (prev) => (prev ? { ...prev, status: "REFUSED" } : prev),
      )
      setIsRefusing(false)
    },
    onError: (err: Error) => {
      toast({ title: "Refus impossible", description: err.message, variant: "destructive" })
    },
  })

  if (!token) {
    return (
      <PublicShell>
        <ErrorState title="Lien invalide" message="Aucun jeton de signature n'a été fourni." />
      </PublicShell>
    )
  }

  if (query.isLoading) {
    return (
      <PublicShell>
        <div className="py-16 text-center text-muted-foreground" data-test="vc-loading">
          Chargement du contrat...
        </div>
      </PublicShell>
    )
  }

  if (query.isError || !query.data) {
    const errAny = query.error as (Error & { status?: number }) | null
    const status = errAny?.status
    // 410 : le serveur dit pourquoi (annulée, refusée, expirée).
    // Sans son message, un texte commun aux trois cas.
    if (status === HTTP_GONE) {
      return (
        <PublicShell>
          <ErrorState
            title={GONE_TITLE}
            message={pipe(serverDetail(query.error), O.getOrElse(() => GONE_FALLBACK))}
            dataTest="vc-revoked-state"
          />
        </PublicShell>
      )
    }
    if (status === 404) {
      return (
        <PublicShell>
          <ErrorState
            title="Lien introuvable"
            message="Ce lien de signature est inconnu ou n'existe plus. Demandez un nouveau lien à l'organisme."
            dataTest="vc-revoked-state"
          />
        </PublicShell>
      )
    }
    return (
      <PublicShell>
        <ErrorState
          title="Contrat introuvable"
          message={errAny?.message ?? "Impossible de charger le contrat."}
          dataTest="vc-error-state"
        />
      </PublicShell>
    )
  }

  const contract = query.data
  const canSign = contract.status === "PENDING_SIGNATURE"
  const isSigned = contract.status === "SIGNED"
  const isRefused = contract.status === "REFUSED"

  return (
    <PublicShell tenantName={contract.tenant.name} tenantLogo={contract.tenant.logo_url}>
      <div className="space-y-6" data-test="public-vendor-contract-page">
        <StatusHeader contract={contract} />

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
            <div>
              <CardTitle data-test="vc-contract-ref">
                Contrat de sous-traitance {contract.contract_ref}
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground" data-test="vc-vendor-name">
                {contract.vendor.name}
              </p>
            </div>
            {contract.pdf_url ? (
              <Button asChild variant="outline" size="sm" data-test="vc-download-pdf">
                <a href={publicVendorContractPdfUrl(token)} target="_blank" rel="noopener noreferrer">
                  <Download className="mr-2 h-4 w-4" />
                  Lire le PDF
                </a>
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-muted-foreground">Organisme de formation</dt>
                <dd className="font-medium">{contract.tenant.name}</dd>
              </div>
              {contract.vendor.siret ? (
                <div>
                  <dt className="text-xs text-muted-foreground">SIRET du sous-traitant</dt>
                  <dd className="font-mono">{contract.vendor.siret}</dd>
                </div>
              ) : null}
              {contract.vendor.legal_representative ? (
                <div>
                  <dt className="text-xs text-muted-foreground">Représentant</dt>
                  <dd>{contract.vendor.legal_representative}</dd>
                </div>
              ) : null}
              {contract.expires_at && canSign ? (
                <div>
                  <dt className="text-xs text-muted-foreground">À signer avant le</dt>
                  <dd>{formatDateTime(contract.expires_at)}</dd>
                </div>
              ) : null}
            </dl>
          </CardContent>
        </Card>

        {isSigned ? (
          <SignedConfirmation contract={contract} />
        ) : isRefused ? (
          <Card className="border-rose-200 bg-rose-50" data-test="vc-refused-confirmation">
            <CardContent className="space-y-1 pt-6 text-sm text-rose-900">
              <div className="flex items-center gap-2 font-medium">
                <FileX className="h-4 w-4" />
                Vous avez refusé ce contrat
              </div>
              <p className="text-xs">L'organisme de formation a été informé de votre refus.</p>
            </CardContent>
          </Card>
        ) : canSign ? (
          <SignaturePanel
            legalNotice={contract.legal_notice}
            signerName={signerName}
            onSignerNameChange={setSignerName}
            consentChecked={consentChecked}
            onConsentChange={setConsentChecked}
            onSign={() => signMutation.mutate()}
            isSigning={signMutation.isPending}
            isRefusing={isRefusing}
            onStartRefuse={() => setIsRefusing(true)}
            onCancelRefuse={() => setIsRefusing(false)}
            refuseReason={refuseReason}
            onRefuseReasonChange={setRefuseReason}
            onRefuse={() => refuseMutation.mutate()}
            isRefuseSubmitting={refuseMutation.isPending}
          />
        ) : null}

        <GdprNotice />
      </div>
    </PublicShell>
  )
}

function PublicShell({
  children,
  tenantName,
  tenantLogo,
}: {
  children: React.ReactNode
  tenantName?: string
  tenantLogo?: string | null
}) {
  return (
    <div className="min-h-screen bg-slate-50" data-test="public-shell">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          {tenantLogo ? (
            <img
              src={tenantLogo}
              alt={tenantName ?? "Logo"}
              className="h-10 w-10 rounded object-contain"
              data-test="tenant-logo"
            />
          ) : (
            <span className="flex h-10 w-10 items-center justify-center rounded bg-slate-100 text-slate-500">
              <Building2 className="h-5 w-5" />
            </span>
          )}
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Signature électronique</p>
            <p className="truncate text-sm font-medium text-slate-900" data-test="public-tenant-name">
              {tenantName ?? "Organisme de formation"}
            </p>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">{children}</main>
      <footer className="mx-auto max-w-3xl px-4 py-6 text-center text-xs text-muted-foreground sm:px-6">
        Document généré par Qualneo
      </footer>
    </div>
  )
}

function StatusHeader({ contract }: { contract: PublicVendorContractResponse }) {
  const meta = statusMeta(contract.status)
  const Icon = meta.icon
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-4 ${meta.containerClass}`}
      data-test="vc-status-banner"
      data-status={contract.status}
    >
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0" />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-semibold" data-test="vc-status-label">
          {meta.title}
        </p>
        <p className="text-sm text-muted-foreground" data-test="vc-status-message">
          {meta.subtitle(contract)}
        </p>
        <p className="text-xs text-muted-foreground" data-test="vc-version">
          Version {contract.version_number}
        </p>
      </div>
    </div>
  )
}

function statusMeta(status: PublicVendorContractStatus): {
  icon: React.ComponentType<{ className?: string }>
  containerClass: string
  title: string
  subtitle: (c: PublicVendorContractResponse) => string
} {
  switch (status) {
    case "PENDING_SIGNATURE":
      return {
        icon: FileSignature,
        containerClass: "border-amber-200 bg-amber-50 text-amber-900",
        title: "Signature attendue",
        subtitle: () => "Votre signature est requise pour valider ce contrat de sous-traitance.",
      }
    case "SIGNED":
      return {
        icon: FileCheck,
        containerClass: "border-emerald-200 bg-emerald-50 text-emerald-900",
        title: "Contrat signé",
        subtitle: (c) => `Signature enregistrée le ${formatDateTime(c.signed_at)}.`,
      }
    case "REFUSED":
      return {
        icon: FileX,
        containerClass: "border-rose-200 bg-rose-50 text-rose-900",
        title: "Contrat refusé",
        subtitle: () => "Vous avez refusé ce contrat. L'organisme en a été informé.",
      }
    case "REVOKED":
      return {
        icon: FileX,
        containerClass: "border-rose-200 bg-rose-50 text-rose-900",
        title: "Lien révoqué",
        subtitle: () => "Ce lien a été révoqué par l'organisme de formation.",
      }
    case "SIGNATURE_EXPIRED":
      return {
        icon: CalendarClock,
        containerClass: "border-slate-200 bg-slate-50 text-slate-900",
        title: "Lien expiré",
        subtitle: () => "La période de validité du lien est dépassée. Contactez l'organisme.",
      }
  }
}

function SignaturePanel({
  legalNotice,
  signerName,
  onSignerNameChange,
  consentChecked,
  onConsentChange,
  onSign,
  isSigning,
  isRefusing,
  onStartRefuse,
  onCancelRefuse,
  refuseReason,
  onRefuseReasonChange,
  onRefuse,
  isRefuseSubmitting,
}: {
  legalNotice: string
  signerName: string
  onSignerNameChange: (v: string) => void
  consentChecked: boolean
  onConsentChange: (checked: boolean) => void
  onSign: () => void
  isSigning: boolean
  isRefusing: boolean
  onStartRefuse: () => void
  onCancelRefuse: () => void
  refuseReason: string
  onRefuseReasonChange: (v: string) => void
  onRefuse: () => void
  isRefuseSubmitting: boolean
}) {
  const canSubmit = signerName.trim().length > 0 && consentChecked
  return (
    <Card data-test="vc-signature-panel">
      <CardHeader>
        <CardTitle className="text-base">Signer ce contrat</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {legalNotice ? (
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-test="vc-legal-notice">
            {legalNotice}
          </p>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="vc-signer-name">Votre nom complet</Label>
          <Input
            id="vc-signer-name"
            value={signerName}
            onChange={(e) => onSignerNameChange(e.target.value)}
            placeholder="Prénom Nom"
            data-test="vc-signer-name"
          />
        </div>
        <label className="flex items-start gap-2 text-xs text-muted-foreground" data-test="vc-consent-label">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => onConsentChange(e.currentTarget.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300"
            data-test="vc-consent-checkbox"
          />
          <span>
            J'ai lu le contrat, j'en accepte le contenu et je consens à l'enregistrement de mon adresse IP, de l'horodatage et de mon identité comme preuve d'authenticité (signature électronique simple, article 1366 du Code civil).
          </span>
        </label>
        <Button
          onClick={onSign}
          disabled={!canSubmit || isSigning}
          className="w-full bg-rose-500 hover:bg-rose-600"
          data-test="vc-sign-button"
        >
          {isSigning ? "Signature en cours..." : "Accepter et signer"}
        </Button>

        {isRefusing ? (
          <div className="space-y-2 rounded-md border border-rose-200 bg-rose-50 p-3" data-test="vc-refuse-panel">
            <Label htmlFor="vc-refuse-reason" className="text-rose-900">
              Motif du refus
            </Label>
            <Textarea
              id="vc-refuse-reason"
              rows={3}
              value={refuseReason}
              onChange={(e) => onRefuseReasonChange(e.target.value)}
              placeholder="Précisez la raison de votre refus..."
              data-test="vc-refuse-reason"
            />
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onCancelRefuse}>
                Annuler
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={onRefuse}
                disabled={refuseReason.trim().length === 0 || isRefuseSubmitting}
                data-test="vc-confirm-refuse"
              >
                {isRefuseSubmitting ? "Envoi..." : "Confirmer le refus"}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onStartRefuse}
            className="w-full text-center text-xs text-muted-foreground underline hover:text-foreground"
            data-test="vc-refuse-button"
          >
            Refuser ce contrat
          </button>
        )}
      </CardContent>
    </Card>
  )
}

function SignedConfirmation({ contract }: { contract: PublicVendorContractResponse }) {
  return (
    <Card className="border-emerald-200 bg-emerald-50" data-test="vc-signed-confirmation">
      <CardContent className="space-y-2 pt-6 text-sm text-emerald-900">
        <div className="flex items-center gap-2 font-medium">
          <FileCheck className="h-4 w-4" />
          Contrat signé le {formatDateTime(contract.signed_at)}
        </div>
        <p className="text-xs">
          Une copie du contrat signé vous a été envoyée par email. Conservez-la précieusement.
        </p>
      </CardContent>
    </Card>
  )
}

function GdprNotice() {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 text-xs leading-relaxed text-muted-foreground"
      data-test="vc-gdpr-notice"
    >
      <div className="mb-2 flex items-center gap-2 font-medium text-slate-700">
        <Shield className="h-4 w-4" />
        Information RGPD
      </div>
      <p>
        En signant, vous acceptez l'enregistrement de votre adresse IP, de l'horodatage et de votre identité comme preuve d'authenticité (base légale article 6.1.b du RGPD, mesures précontractuelles à votre demande, conservation 10 ans). Pour exercer vos droits, contactez le délégué à la protection des données de l'organisme ou la CNIL.
      </p>
    </div>
  )
}

function ErrorState({
  title,
  message,
  dataTest = "vc-error-state",
}: {
  title: string
  message: string
  dataTest?: string
}) {
  return (
    <Card data-test={dataTest}>
      <CardContent className="space-y-2 py-12 text-center">
        <h2 className="text-lg font-semibold" data-test={`${dataTest}-title`}>{title}</h2>
        <p className="text-sm text-muted-foreground" data-test={`${dataTest}-message`}>{message}</p>
      </CardContent>
    </Card>
  )
}
